/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the application.
 * Manages user sessions, login, logout, and role-based access.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';

interface User {
  id: number;
  username: string;
  role: 'Administrator' | 'Fachbereich Manager' | 'Fachbereich Administrator' | 'Beobachter';
  fachbereiche?: string[];
  passwordResetRequired?: boolean;
  isDeveloper?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  year: number | null;
  loading: boolean;
  login: (username: string, password: string, year?: number) => Promise<{ requiresYearSelection?: boolean; availableYears?: number[] } | void>;
  logout: () => Promise<void>;
  isAdmin: () => boolean;
  isViewer: () => boolean;
  canRegisterHelpers: () => boolean;
  canViewAllFachbereiche: () => boolean;
  canUseGoogleFormOrSync: () => boolean;
  canEmailVisibleHelpers: () => boolean;
  refreshUser: () => Promise<void>;
  changeYear: (year: number) => Promise<void>;
  availableYears: number[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [year, setYear] = useState<number | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  // Configure axios to include auth token in requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Suppress 401 errors in console as they're expected when not authenticated
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Suppress 401 errors in console - they're expected when not authenticated
        if (error.response?.status === 401) {
          // Silently handle 401 errors
          return Promise.reject(error);
        }
        // Let other errors through normally
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Load token, year and user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedYear = localStorage.getItem('authYear');
    if (storedToken) {
      setToken(storedToken);
      if (storedYear) {
        setYear(parseInt(storedYear, 10));
      }
      // Verify token and get user info
      refreshUser();
    } else {
      setLoading(false);
    }
  }, []);

  const refreshUser = async (skipLoading = false) => {
    try {
      const storedToken = localStorage.getItem('authToken');
      if (!storedToken) {
        setUser(null);
        setYear(null);
        if (!skipLoading) {
          setLoading(false);
        }
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`);
      if (response.data.success) {
        setUser(response.data.user);
        setToken(storedToken);
        if (response.data.year) {
          setYear(response.data.year);
          localStorage.setItem('authYear', response.data.year.toString());
        }
        // Fetch available years
        try {
          const yearsResponse = await axios.get(`${API_BASE_URL}/api/auth/available-years`);
          if (yearsResponse.data.success) {
            setAvailableYears(yearsResponse.data.years || []);
          }
        } catch (yearsError) {
          // Silently fail - available years are not critical
          console.warn('Could not fetch available years:', yearsError);
        }
      } else {
        // Token invalid, clear it
        localStorage.removeItem('authToken');
        localStorage.removeItem('authYear');
        setToken(null);
        setUser(null);
        setYear(null);
      }
    } catch (error: any) {
      // Token invalid or expired - suppress 401 errors as they're expected when not authenticated
      if (error.response?.status === 401) {
        // Expected when not authenticated, silently handle
      } else {
        // Only log unexpected errors
        console.error('Error refreshing user:', error);
      }
      localStorage.removeItem('authToken');
      localStorage.removeItem('authYear');
      setToken(null);
      setUser(null);
      setYear(null);
    } finally {
      if (!skipLoading) {
        setLoading(false);
      }
    }
  };

  const login = async (username: string, password: string, year?: number) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        username,
        password,
        year
      });

      if (response.data.success) {
        // Check if year selection is required
        if (response.data.requiresYearSelection) {
          setAvailableYears(response.data.availableYears || []);
          return { requiresYearSelection: true, availableYears: response.data.availableYears || [] };
        }

        const { token: newToken, year: responseYear } = response.data;
        localStorage.setItem('authToken', newToken);
        if (responseYear) {
          localStorage.setItem('authYear', responseYear.toString());
          setYear(responseYear);
        }
        setToken(newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        // Refresh user data to get complete user info including Fachbereiche
        // Pass skipLoading=true to avoid interfering with login loading state
        await refreshUser(true);
        setLoading(false);
      } else {
        throw new Error(response.data.message || 'Anmeldung fehlgeschlagen');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Anmeldung fehlgeschlagen';
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API_BASE_URL}/api/auth/logout`);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authYear');
      setToken(null);
      setUser(null);
      setYear(null);
      setAvailableYears([]);
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const changeYear = async (newYear: number) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/change-year`, {
        year: newYear
      });

      if (response.data.success) {
        setYear(newYear);
        localStorage.setItem('authYear', newYear.toString());
        // Refresh user data to get Fachbereiche for the new year
        await refreshUser(true);
        // Refresh available years
        try {
          const yearsResponse = await axios.get(`${API_BASE_URL}/api/auth/available-years`);
          if (yearsResponse.data.success) {
            setAvailableYears(yearsResponse.data.years || []);
          }
        } catch (yearsError) {
          // Silently fail
          console.warn('Could not refresh available years:', yearsError);
        }
      } else {
        throw new Error(response.data.message || 'Jahr konnte nicht geändert werden');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Jahr konnte nicht geändert werden';
      throw new Error(message);
    }
  };

  const isAdmin = () => {
    return user?.role === 'Administrator';
  };

  const isViewer = () => {
    return user?.role === 'Beobachter';
  };

  const canRegisterHelpers = () => {
    return user?.role === 'Administrator' || user?.role === 'Fachbereich Administrator';
  };

  const canViewAllFachbereiche = () => {
    return isAdmin() || isViewer();
  };

  const canUseGoogleFormOrSync = () => {
    return canRegisterHelpers() || isViewer();
  };

  const canEmailVisibleHelpers = () => {
    return user?.role === 'Administrator' || user?.role === 'Fachbereich Administrator' || user?.role === 'Fachbereich Manager';
  };

  return (
    <AuthContext.Provider value={{ user, token, year, loading, login, logout, isAdmin, isViewer, canRegisterHelpers, canViewAllFachbereiche, canUseGoogleFormOrSync, canEmailVisibleHelpers, refreshUser, changeYear, availableYears }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

